﻿import { MapWithPathSupport } from "../../shared/utilities";
export interface Storyline {
    name: string;
    schemaVersion: string;
    dataSources: { [name: string]: DataSource };
    templates: { [name: string]: TemplateDefinition };
    chapters: Chapter[];
    canHideChapter?: boolean;
    canHidePage?: boolean;
}

export interface Chapter {
    name: string;
    dataSources: { [name: string]: DataSource };
    pages: Page[];
}

export interface Page {
    name: string;
    dataSources: { [name: string]: DataSource };
    template: string;
    paragraphs: Paragraph[];
}

export interface Paragraph {
    name: string;
    dataSources: { [name: string]: Slot };
    slots: Slot[];
}

export interface Slot {
    name: string;
    dataSource?: string;
}

export interface Template {
    name: string;
    root: TemplateDefinition;
}

export interface TemplateDefinition {
    jsx: string;
    css: string;
    // Client-side only: incremented on every content change, so consumers can use `name:version` as a cheap identity key instead of the full JSX string...
    version?: number;
}

export interface DataSource {
    name: string;
    parameters: DataSourceParameter[];
    defaultData?: unknown;
    autoRefresh: boolean;
    apiEndpoint?: string[];
    allowLazyLoading?: boolean;
}

export interface DataSourceParameter {
    name: string;
    type: string;
    isRequired: boolean;
    defaultValue?: any;
}

export interface SnapshotPosition {
    chapterIndex: number;
    pageIndex: number;
    paragraphIndex: number;
}

// Present in the slice while viewing a snapshot - its presence is what disables datasource refetches...
export interface SnapshotContext {
    snapshotId: string;
    owner: string;
    name?: string;
    capturedAt: string;
    storylineName: string;
    capturedUrl: string;
    branch: string;
}

export interface StorylineState {
    id: string;
    name: string;
    storyline: Storyline;
    datasources: Map<string, DataSource>;
    datasourceValues: Map<string, unknown>;
    datasourceErrors: Map<string, any>;
    datasourcesInFlight: Set<string>;
    parameterValues: MapWithPathSupport<unknown>;
    canvasState: Object;
    snapshot: SnapshotContext | null;

    canvases: Canvas[][][];

    chapterIndex: number;
    pageIndex: number;
    paragraphIndex: number;

    currentParagraph: Canvas;
    loading: boolean;

    canNavigateBackwards: boolean;
    canNavigateForward: boolean;
}

export interface Canvas {
    name: string;
    chapterName: string;
    pageName: string;
    title: string;
    data: object;
    template: TemplateDefinition;
    templateName: string;
}

export interface NavigationTarget {
    chapterIndex: number,
    pageIndex: number,
    paragraphIndex: number,
    previousParagraphIndex?: number
}

export const LOAD_STORYLINE = "LOAD_STORYLINE";
export const SHOW_STORYLINE = "SHOW_STORYLINE";
export const SHOW_SNAPSHOT = "SHOW_SNAPSHOT";
export const PARAMETER_VALUE_UPDATED = "PARAMETER_VALUE_UPDATED";
export const DATASOURCE_UPDATED = "DATASOURCE_UPDATED";
export const DATASOURCE_ERROR_OCCURRED = "DATASOURCE_ERROR_OCCURRED";
export const GO_TO_X_Y_Z = "GO_TO_X_Y_Z";
export const GO_TO_ID = "GO_TO_ID";
export const UPDATE_CURRENT_SLIDE_TEMPLATE = "UPDATE_CURRENT_SLIDE_TEMPLATE";
export const UPDATE_SLIDE_TEMPLATES = "UPDATE_SLIDE_TEMPLATES";
export const UPDATE_CURRENT_FRAME_DATA = "UPDATE_CURRENT_FRAME_DATA";
export const UPDATE_CANVAS_STATE = "UPDATE_CANVAS_STATE";
export const ADD_GLOBAL_DATASOURCE = "ADD_GLOBAL_DATASOURCE";
export const UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE = "UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE";

export interface LoadStorylineAction {
    type: typeof LOAD_STORYLINE,
    id: string
}

export interface ShowStorylineAction {
    id: string,
    type: typeof SHOW_STORYLINE,
    storyline: Storyline,
    dataSources: Map<string, DataSource>,
    dataSourceValues: Map<string, unknown>,
    parameterValues: Map<string, unknown>
}

export interface ShowSnapshotAction {
    type: typeof SHOW_SNAPSHOT,
    storyline: Storyline,
    dataSources: Map<string, DataSource>,
    dataSourceValues: Map<string, unknown>,
    parameterValues: MapWithPathSupport<unknown>,
    canvasState: object,
    position: SnapshotPosition,
    snapshot: SnapshotContext
}

export interface ParameterValueUpdatedAction {
    type: typeof PARAMETER_VALUE_UPDATED,
    parameterName: string,
    newValue: any
}

export interface DatasourceUpdatedAction {
    type: typeof DATASOURCE_UPDATED,
    datasourceName: string,
    data: object
}

export interface DatasourceErrorOccurredAction {
    type: typeof DATASOURCE_ERROR_OCCURRED,
    datasourceName: string,
    error: any
}

export interface GoToXYZAction {
    type: typeof GO_TO_X_Y_Z,
    chapterIndex: number,
    pageIndex: number,
    paragraphIndex: number,
    previousParagaphIndex?: number
}

export interface GoToIDAction {
    type: typeof GO_TO_ID,
    id: string
}

// Which authoring surface pushed a template update; used to attribute the TemplateUpdated trace event.
export type TemplateUpdateOrigin = "DevTools" | "VsCodeExtension";

export interface UpdateCurrentSlideTemplate {
    type: typeof UPDATE_CURRENT_SLIDE_TEMPLATE,
    newTemplate: string,
    newCustomCss: string,
    origin?: TemplateUpdateOrigin
}

export interface UpdateSlideTemplates {
    type: typeof UPDATE_SLIDE_TEMPLATES,
    newTemplates: Record<string, TemplateDefinition>,
    origin?: TemplateUpdateOrigin
}

export interface UpdateCurrentFrameData {
    type: typeof UPDATE_CURRENT_FRAME_DATA,
    newData: object
}

export interface UpdateCanvasState {
    type: typeof UPDATE_CANVAS_STATE,
    newState: any
}

export interface AddGlobalDatasource {
    type: typeof ADD_GLOBAL_DATASOURCE,
    datasource: DataSource
}

export interface UpdateInFlightRequestsForDatasource {
    type: typeof UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE,
    datasource: DataSource,
    inFlightRequestCount: number
}

export type StorylineActionTypes = LoadStorylineAction | ShowStorylineAction | ShowSnapshotAction | ParameterValueUpdatedAction | DatasourceUpdatedAction | GoToXYZAction | GoToIDAction | UpdateCurrentSlideTemplate | UpdateSlideTemplates | UpdateCurrentFrameData | UpdateCanvasState | AddGlobalDatasource | UpdateInFlightRequestsForDatasource | DatasourceErrorOccurredAction;